// src/pages/stockTransfer/CreateStockTransferPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftRight, X, Save, CheckCircle, AlertCircle, Loader2, Warehouse as WarehouseIcon } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import DatePicker from '../../components/ui/DatePicker/DatePicker';
import TransferItemsSection from './components/TransferItemsSection';
import {
  getAccessibleWarehouses,
  getAllWarehouses,
  getStockTransferById,
  createStockTransfer,
  updateStockTransfer,
  completeStockTransfer
} from '../../services/api/stockTransfer';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getTransferItemDisplayName, getTransferItemSku, getTransferItemImage } from './transferItemUtils';
import styles from './CreateStockTransferPage.module.css';

const today = () => new Date().toISOString().split('T')[0];

const CreateStockTransferPage = () => {
  const navigate = useNavigate();
  const { transferId } = useParams();
  const isEdit = Boolean(transferId);
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [sourceWarehouses, setSourceWarehouses] = useState([]);
  const [allWarehouses, setAllWarehouses] = useState([]);
  const [form, setForm] = useState({ fromWarehouseId: '', toWarehouseId: '', transferDate: today(), notes: '' });
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, [transferId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [accessible, all] = await Promise.all([getAccessibleWarehouses(), getAllWarehouses()]);
      setSourceWarehouses(accessible.warehouses || []);
      setAllWarehouses(all.warehouses || []);

      if (isEdit) {
        const { transfer } = await getStockTransferById(transferId);
        if (transfer.status !== 'draft') {
          navigate(`/inventory/stock-transfers/${transferId}`, { replace: true });
          return;
        }
        setForm({
          fromWarehouseId: transfer.fromWarehouseId.toString(),
          toWarehouseId: transfer.toWarehouseId.toString(),
          transferDate: transfer.transferDate || today(),
          notes: transfer.notes || ''
        });
        setItems((transfer.items || []).map(item => ({
          productId: item.productId,
          variationId: item.variationId,
          displayName: getTransferItemDisplayName(item),
          sku: getTransferItemSku(item),
          image: getTransferItemImage(item),
          fromStock: null,
          quantity: parseFloat(item.quantity)
        })));
      } else if (accessible.warehouses?.length === 1) {
        setForm(prev => ({ ...prev, fromWarehouseId: accessible.warehouses[0].id.toString() }));
      }
    } catch (err) {
      console.error('Failed to load transfer data:', err);
      error(err.response?.data?.message || t('stockTransfer.failedToLoad'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFromChange = (value) => {
    if (items.length > 0 && !window.confirm(t('stockTransfer.confirmChangeFrom'))) return;
    setItems([]);
    setForm(prev => ({
      ...prev,
      fromWarehouseId: value,
      toWarehouseId: prev.toWarehouseId === value ? '' : prev.toWarehouseId
    }));
  };

  const handleAddItem = (newItem) => {
    setErrors(prev => ({ ...prev, items: null }));
    setItems(prev => {
      const index = prev.findIndex(item =>
        item.productId === newItem.productId && (item.variationId || null) === (newItem.variationId || null)
      );
      if (index === -1) return [...prev, newItem];
      return prev.map((item, i) => i === index
        ? { ...item, quantity: (parseFloat(item.quantity) || 0) + newItem.quantity }
        : item);
    });
  };

  const handleUpdateItem = (index, quantity) => {
    setItems(prev => prev.map((item, i) => (i === index ? { ...item, quantity } : item)));
  };

  const handleRemoveItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.fromWarehouseId) newErrors.fromWarehouseId = t('stockTransfer.fromWarehouseRequired');
    if (!form.toWarehouseId) newErrors.toWarehouseId = t('stockTransfer.toWarehouseRequired');
    if (form.fromWarehouseId && form.fromWarehouseId === form.toWarehouseId) {
      newErrors.toWarehouseId = t('stockTransfer.sameWarehouseError');
    }
    if (items.length === 0) newErrors.items = t('stockTransfer.itemsRequired');
    else if (items.some(item => !(parseFloat(item.quantity) > 0))) newErrors.items = t('stockTransfer.invalidQuantity');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveTransfer = async () => {
    const payload = {
      fromWarehouseId: parseInt(form.fromWarehouseId),
      toWarehouseId: parseInt(form.toWarehouseId),
      transferDate: form.transferDate || null,
      notes: form.notes || null,
      items: items.map(item => ({
        productId: item.productId,
        variationId: item.variationId || null,
        quantity: parseFloat(item.quantity)
      }))
    };
    const response = isEdit
      ? await updateStockTransfer(transferId, payload)
      : await createStockTransfer(payload);
    return response.transfer;
  };

  const handleSaveDraft = async () => {
    if (!validate()) return;
    try {
      setIsSaving(true);
      const transfer = await saveTransfer();
      success(t('stockTransfer.draftSaved'));
      navigate(`/inventory/stock-transfers/${transfer.id}`);
    } catch (err) {
      console.error('Failed to save transfer:', err);
      error(err.response?.data?.message || t('stockTransfer.failedToSave'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCompleteClick = () => {
    if (validate()) setShowCompleteDialog(true);
  };

  const confirmComplete = async () => {
    let saved = null;
    try {
      setIsSaving(true);
      saved = await saveTransfer();
      await completeStockTransfer(saved.id);
      success(t('stockTransfer.transferCompleted'));
      navigate(`/inventory/stock-transfers/${saved.id}`);
    } catch (err) {
      console.error('Failed to complete transfer:', err);
      error(err.response?.data?.message || t('stockTransfer.failedToComplete'));
      if (saved && !isEdit) navigate(`/inventory/stock-transfers/${saved.id}/edit`, { replace: true });
    } finally {
      setIsSaving(false);
      setShowCompleteDialog(false);
    }
  };

  const destinationWarehouses = allWarehouses.filter(w => w.id.toString() !== form.fromWarehouseId);
  const fromName = allWarehouses.find(w => w.id.toString() === form.fromWarehouseId)?.name
    || sourceWarehouses.find(w => w.id.toString() === form.fromWarehouseId)?.name;
  const toName = allWarehouses.find(w => w.id.toString() === form.toWarehouseId)?.name;
  const totalQuantity = items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);

  const header = (
    <PageHeader
      title={isEdit ? t('stockTransfer.editStockTransfer') : t('stockTransfer.createStockTransfer')}
      description={t('stockTransfer.pageDescription')}
      icon={ArrowLeftRight}
      actions={
        <button onClick={() => navigate('/inventory/stock-transfers')} className={styles.backButton}>
          <X size={20} />
          {t('stockTransfer.backToTransfers')}
        </button>
      }
    />
  );

  if (isLoading) {
    return (
      <div className={styles.stockTransferPage}>
        {header}
        <div className={styles.loadingState}>
          <Loader2 className={styles.spinning} size={48} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stockTransferPage}>
      {header}

      <div className={styles.sectionCard}>
        <h2>
          <WarehouseIcon size={20} />
          {t('stockTransfer.warehouses')}
        </h2>
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label>{t('stockTransfer.fromWarehouse')} *</label>
            <Dropdown
              value={form.fromWarehouseId ? parseInt(form.fromWarehouseId) : ''}
              onChange={(value) => handleFromChange(value ? value.toString() : '')}
              options={sourceWarehouses.map(w => ({ value: w.id, label: w.name }))}
              placeholder={t('stockTransfer.selectWarehouse')}
              searchable
              className={errors.fromWarehouseId ? styles.inputError : ''}
            />
            {errors.fromWarehouseId && <span className={styles.fieldError}>{errors.fromWarehouseId}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>{t('stockTransfer.toWarehouse')} *</label>
            <Dropdown
              value={form.toWarehouseId ? parseInt(form.toWarehouseId) : ''}
              onChange={(value) => setForm(prev => ({ ...prev, toWarehouseId: value ? value.toString() : '' }))}
              options={destinationWarehouses.map(w => ({ value: w.id, label: w.name }))}
              placeholder={t('stockTransfer.selectWarehouse')}
              searchable
              className={errors.toWarehouseId ? styles.inputError : ''}
            />
            {errors.toWarehouseId && <span className={styles.fieldError}>{errors.toWarehouseId}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>{t('stockTransfer.transferDate')}</label>
            <DatePicker
              value={form.transferDate}
              onChange={(value) => setForm(prev => ({ ...prev, transferDate: value || '' }))}
              placeholder={t('stockTransfer.transferDate')}
            />
          </div>

          <div className={`${styles.formGroup} ${styles.fullWidth}`}>
            <label>{t('stockTransfer.notes')}</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder={t('stockTransfer.notesPlaceholder')}
              className={styles.textarea}
              rows={2}
            />
          </div>
        </div>
      </div>

      <div className={styles.sectionCard}>
        <div className={styles.itemsHeader}>
          <h2>{t('stockTransfer.items')}</h2>
          <div className={styles.itemsSummary}>
            {items.length} {t('stockTransfer.itemsLabel')} · {t('stockTransfer.totalQuantity')}: <strong>{totalQuantity}</strong>
          </div>
        </div>
        <TransferItemsSection
          warehouseId={form.fromWarehouseId ? parseInt(form.fromWarehouseId) : null}
          items={items}
          errors={errors}
          onAddItem={handleAddItem}
          onUpdateItem={handleUpdateItem}
          onRemoveItem={handleRemoveItem}
        />
      </div>

      <div className={styles.actionBar}>
        <button onClick={handleSaveDraft} className={styles.secondaryButton} disabled={isSaving}>
          <Save size={18} />
          {t('stockTransfer.saveDraft')}
        </button>
        {hasPermission('complete_stock_transfer') && (
          <button onClick={handleCompleteClick} className={styles.primaryButton} disabled={isSaving}>
            <CheckCircle size={18} />
            {t('stockTransfer.saveAndComplete')}
          </button>
        )}
      </div>

      {showCompleteDialog && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <AlertCircle size={24} className={styles.warningIcon} />
              <h3>{t('stockTransfer.confirmCompleteTitle')}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>{t('stockTransfer.confirmCompleteMessage')}</p>
              <div className={styles.summaryBox}>
                <div className={styles.summaryRow}><span>{t('stockTransfer.fromWarehouse')}:</span><strong>{fromName}</strong></div>
                <div className={styles.summaryRow}><span>{t('stockTransfer.toWarehouse')}:</span><strong>{toName}</strong></div>
                <div className={styles.summaryRow}><span>{t('stockTransfer.totalItems')}:</span><strong>{items.length}</strong></div>
                <div className={styles.summaryRow}><span>{t('stockTransfer.totalQuantity')}:</span><strong>{totalQuantity}</strong></div>
              </div>
            </div>
            <div className={styles.dialogActions}>
              <button onClick={() => setShowCompleteDialog(false)} className={styles.secondaryButton} disabled={isSaving}>
                {t('common.cancel')}
              </button>
              <button onClick={confirmComplete} className={styles.primaryButton} disabled={isSaving}>
                {isSaving ? <Loader2 className={styles.spinning} size={16} /> : <CheckCircle size={16} />}
                {t('stockTransfer.complete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateStockTransferPage;
